import { createStyles } from '@mantine/core';
import { match } from 'ts-pattern';

import Image from '~/domains/common/components/Image/Image';

import { useQuickPicksFilters } from '../../hooks/useQuickPicksFilters';

import { IconMapByLeague } from './IconMap';

type LeagueFallbackImageProps = {
  league: string;
  leagueIconUrl: string | undefined;
  teamLogoUrl?: string;
  size: number;
  className?: string;
  color?: string;
  applySolidColor?: boolean;
};

const useStyles = createStyles(
  (
    _theme,
    {
      leagueIconUrl = '',
      teamLogoUrl = '',
      size,
      color = '',
    }: {
      leagueIconUrl: string;
      teamLogoUrl: string;
      size: number;
      color: string;
    }
  ) => {
    const solidIconBase = {
      display: 'inline-block',
      width: size,
      height: size,
      backgroundColor: color,
    };
    const getSolidMaskStyle = (maskUrl: string) => ({
      ...solidIconBase,
      maskImage: `url("${maskUrl}")`,
      maskSize: 'contain',
      maskRepeat: 'no-repeat',
      maskPosition: 'center',
      WebkitMaskImage: `url("${maskUrl}")`,
      WebkitMaskSize: 'contain',
      WebkitMaskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
    });

    return {
      solidLeagueIcon: getSolidMaskStyle(leagueIconUrl),
      solidTeamIcon: getSolidMaskStyle(teamLogoUrl),
    };
  }
);

export function LeagueFallbackImage({
  league,
  leagueIconUrl,
  teamLogoUrl,
  size,
  className,
  color,
  applySolidColor = false,
}: LeagueFallbackImageProps) {
  const { data: filtersData } = useQuickPicksFilters();
  const accessibleLeagueLabel =
    filtersData?.filters.leagues.find((l) => l.id.toLowerCase() === league.toLowerCase())
      ?.display_name ?? league.toUpperCase();

  const svgFallback = <IconMapByLeague leagueId={league} size={size} color={color} />;
  const shouldRenderSolidIcon = applySolidColor && !!color;
  const { classes, cx } = useStyles({
    leagueIconUrl,
    teamLogoUrl,
    size,
    color,
  });
  const renderIconNode = (iconUrl: string | undefined, solidClassName: string) =>
    shouldRenderSolidIcon && iconUrl ? (
      <span
        role="img"
        aria-label={accessibleLeagueLabel}
        className={cx(className, solidClassName)}
      />
    ) : (
      <Image
        src={iconUrl}
        alt={accessibleLeagueLabel}
        width={size}
        height={size}
        className={className}
      />
    );
  const leagueIconNode = renderIconNode(leagueIconUrl, classes.solidLeagueIcon);
  const teamLogoNode = renderIconNode(teamLogoUrl, classes.solidTeamIcon);

  const imageProps = { alt: accessibleLeagueLabel, width: size, height: size, className };

  return match([!!teamLogoUrl, !!leagueIconUrl] as const)
    .returnType<React.ReactNode>()
    .with([true, true], () =>
      shouldRenderSolidIcon ? (
        teamLogoNode
      ) : (
        <Image
          src={teamLogoUrl}
          {...imageProps}
          fallbackComponent={leagueIconNode || svgFallback}
        />
      )
    )
    .with([true, false], () =>
      shouldRenderSolidIcon ? (
        teamLogoNode
      ) : (
        <Image src={teamLogoUrl} {...imageProps} fallbackComponent={svgFallback} />
      )
    )
    .with([false, true], () => leagueIconNode)
    .with([false, false], () => svgFallback)
    .exhaustive();
}
